/**
 * Pure formatting/layout logic for the live log panel (owner directive
 * 2026-09-27: "the live log especially" was cramped — timestamps truncated
 * to "2026-09-2", event kinds ran into the dates, filter chips wrapped into
 * two lines). Factored out of `LogPanel.ts` so the row shape, the
 * time/divider math, and the chip-overflow arithmetic are unit-testable
 * without a DOM.
 */

import type { LogEvent, LogEventGroup } from "../types/events";
import { groupForKind } from "../types/events";
import { formatMoney, formatPrice, formatQuantity } from "../format/number";

/** A fixed-width clock reads better in a dense log than a full ISO string
 * (DESIGN.md §4: "row height for list-like content ... tight enough that
 * >=40 rows fit"). Falls back to a placeholder of the same width so the
 * column never reflows when a v1 line's time field is missing/unparsed. */
export function formatClockTime(iso: string): string {
  if (!iso) return "--:--:--";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "--:--:--";
  const h = date.getUTCHours().toString().padStart(2, "0");
  const m = date.getUTCMinutes().toString().padStart(2, "0");
  const s = date.getUTCSeconds().toString().padStart(2, "0");
  return `${h}:${m}:${s}`;
}

/** UTC calendar-day key ("2026-09-26") for grouping day dividers, or `null`
 * when the timestamp can't be parsed (never fabricate a day for an unparsed
 * v1 line). */
export function dayKey(iso: string): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString().slice(0, 10);
}

const MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

/** Human day label for a divider row ("Sep 26, 2026"), derived from the same
 * UTC key `dayKey` computes so the two never disagree. */
export function dayLabel(key: string): string {
  const [yearText, monthText, dayText] = key.split("-");
  const year = Number.parseInt(yearText ?? "", 10);
  const monthIndex = Number.parseInt(monthText ?? "", 10) - 1;
  const day = Number.parseInt(dayText ?? "", 10);
  const month = MONTH_NAMES[monthIndex];
  if (!month || Number.isNaN(year) || Number.isNaN(day)) return key;
  return `${month} ${day}, ${year}`;
}

export type LogRowItem =
  | { readonly type: "divider"; readonly key: string; readonly label: string }
  | {
      readonly type: "wake-header";
      readonly key: string;
      readonly label: string;
      readonly time: string;
    }
  | { readonly type: "event"; readonly key: string; readonly event: LogEvent };

/** Inserts one divider item before the first event of each new UTC day.
 * Events with an unparsed/missing timestamp never trigger a divider (no
 * `dayKey`) — they just render as a plain row, same as today. Preserves the
 * input order otherwise; this is display formatting, not sorting. */
export function buildLogRowItems(events: readonly LogEvent[]): readonly LogRowItem[] {
  const items: LogRowItem[] = [];
  let lastKey: string | null = null;
  for (const event of events) {
    const key = dayKey(event.eventTime) ?? dayKey(event.recordedTime);
    if (key !== null && key !== lastKey) {
      items.push({ type: "divider", key: `divider-${key}-${event.seq}`, label: dayLabel(key) });
      lastKey = key;
    }
    items.push({ type: "event", key: `event-${event.seq}-${event.raw.length}`, event });
  }
  return items;
}

/** The activity feed's default view (orchestrator course correction,
 * 2026-09-28: "signal, not noise") — decisions, orders/fills, portfolio,
 * and life. Everything else (market's per-slot chatter, semantic/research,
 * reconcile, lineage, family, and — deliberately, per the same
 * instruction — runtime/observatory infrastructure bookkeeping) is one
 * click away via the existing per-group filter chips or `"all"`, never
 * gone, just not the default. */
export const DEFAULT_SIGNAL_GROUPS: ReadonlySet<LogEventGroup> = new Set([
  "brain",
  "order",
  "portfolio",
  "life",
]);

/** "Observatory byte accounting" et al — explicitly named in the same
 * instruction as needing its own escape hatch distinct from `"all"`. */
const SYSTEM_GROUPS: ReadonlySet<LogEventGroup> = new Set(["runtime", "observatory"]);

export type ActivityFilter = "signal" | "all" | "system" | LogEventGroup;

function passesActivityFilter(kind: string, filter: ActivityFilter): boolean {
  const group = groupForKind(kind);
  if (filter === "all") return true;
  if (filter === "signal") return DEFAULT_SIGNAL_GROUPS.has(group);
  if (filter === "system") return SYSTEM_GROUPS.has(group);
  return group === filter;
}

const MARKET_BATCH_KIND = "market.view.batch";

function flushMarketBatch(pending: readonly LogEvent[], items: LogRowItem[]): void {
  if (pending.length === 0) return;
  if (pending.length === 1) {
    const event = pending[0]!;
    items.push({ type: "event", key: `event-${event.seq}-${event.raw.length}`, event });
    return;
  }
  // A synthetic row (orchestrator course correction: "SPY/QQQ/AAPL/MSFT/XLK
  // market view updated x5 per minute -> one line"), never presented as a
  // real published event: its own payload says plainly that it's an
  // aggregate, so the detail drawer stays honest about what it is.
  const last = pending[pending.length - 1]!;
  const symbols = Array.from(
    new Set(
      pending
        .map((e) => (typeof e.payload?.["symbol"] === "string" ? e.payload["symbol"] : null))
        .filter((s): s is string => s !== null),
    ),
  );
  const synthetic: LogEvent = {
    seq: last.seq,
    kind: MARKET_BATCH_KIND,
    organismId: last.organismId,
    recordedTime: last.recordedTime,
    eventTime: last.eventTime,
    knowledgeTime: last.knowledgeTime,
    digest: null,
    payload: {
      count: pending.length,
      symbols,
      note: `Synthetic aggregation of ${pending.length} market.view events for legibility (activityGrouping.ts) — not itself a published event; each underlying event still exists in the catalog at its own seq.`,
    },
    raw: "",
  };
  items.push({ type: "event", key: `batch-${pending[0]!.seq}-${last.seq}`, event: synthetic });
}

/** The activity feed's row builder (orchestrator course correction,
 * 2026-09-28): applies `filter`, then walks the result grouping rows under
 * a `brain.wake` header (the wake event itself becomes the header, not
 * also a separate row beneath it) and collapsing consecutive `market.view`
 * events into one summary row. Day dividers still land wherever the UTC
 * day changes, same as `buildLogRowItems`. Pure and order-preserving; the
 * only "sorting" is display grouping, never a reordering of the input. */
export function buildActivityRowItems(
  events: readonly LogEvent[],
  filter: ActivityFilter,
): readonly LogRowItem[] {
  const filtered = events.filter((event) => passesActivityFilter(event.kind, filter));
  const items: LogRowItem[] = [];
  let lastDayKey: string | null = null;
  let pendingMarket: LogEvent[] = [];

  for (const event of filtered) {
    const key = dayKey(event.eventTime) ?? dayKey(event.recordedTime);
    if (key !== null && key !== lastDayKey) {
      flushMarketBatch(pendingMarket, items);
      pendingMarket = [];
      items.push({ type: "divider", key: `divider-${key}-${event.seq}`, label: dayLabel(key) });
      lastDayKey = key;
    }

    if (event.kind === "market.view") {
      pendingMarket.push(event);
      continue;
    }
    if (pendingMarket.length > 0) {
      flushMarketBatch(pendingMarket, items);
      pendingMarket = [];
    }

    if (event.kind === "brain.wake" && event.payload) {
      items.push({
        type: "wake-header",
        key: `wake-${event.seq}`,
        label: activitySentence(event),
        time: formatClockTime(event.eventTime || event.recordedTime),
      });
      continue; // the wake IS the header — not also rendered as a plain row
    }

    items.push({ type: "event", key: `event-${event.seq}-${event.raw.length}`, event });
  }
  flushMarketBatch(pendingMarket, items);
  return items;
}

const PREVIEW_MAX_CHARS = 180;

/** One-line payload preview (the CSS handles the visual ellipsis on
 * overflow; this caps the string itself so a huge payload never bloats the
 * DOM or the detail drawer's row-hover copy). v2 events carry a real
 * payload; v1 log lines never do, so they fall back to whatever the raw
 * line contributes beyond seq/kind/time (already shown in their own
 * columns) — never a placeholder that looks like fabricated data. */
export function payloadPreviewText(event: LogEvent): string {
  if (event.payload && Object.keys(event.payload).length > 0) {
    const compact = JSON.stringify(event.payload);
    return compact.length > PREVIEW_MAX_CHARS
      ? `${compact.slice(0, PREVIEW_MAX_CHARS - 1)}…`
      : compact;
  }
  if (event.organismId) return `organism=${event.organismId}`;
  return "";
}

/** Category colour class for the monospaced kind column (DESIGN.md §5) —
 * one of six existing semantic tokens, reused rather than inventing new
 * hex values. */
const GROUP_COLOR_CLASS: Readonly<Record<LogEventGroup, string>> = {
  brain: "obs-log-kind-brain",
  market: "obs-log-kind-market",
  portfolio: "obs-log-kind-ledger",
  life: "obs-log-kind-ledger",
  family: "obs-log-kind-ledger",
  order: "obs-log-kind-ops",
  reconcile: "obs-log-kind-ops",
  semantic: "obs-log-kind-insight",
  research: "obs-log-kind-insight",
  lineage: "obs-log-kind-insight",
  runtime: "obs-log-kind-quiet",
  observatory: "obs-log-kind-quiet",
  other: "obs-log-kind-quiet",
};

export function logKindColorClass(kind: string): string {
  return GROUP_COLOR_CLASS[groupForKind(kind)];
}

/** The activity feed's icon glyph set (`src/log/activityIcons.ts` draws
 * these) — one of a small, fixed set of categories, never a per-kind icon
 * (that would be dozens of near-identical glyphs nobody could tell apart at
 * 13px). Falls back to the same coarse group the filter chips/kind colour
 * already use for anything not given a more specific icon below. */
export type ActivityIconKind =
  | "wake"
  | "order"
  | "fill"
  | "reconcile"
  | "semantic"
  | "life"
  | "family"
  | "market"
  | "runtime"
  | "alert";

const ICON_BY_GROUP: Readonly<Record<LogEventGroup, ActivityIconKind>> = {
  brain: "wake",
  market: "market",
  portfolio: "fill",
  life: "life",
  family: "family",
  order: "order",
  reconcile: "reconcile",
  semantic: "semantic",
  research: "semantic",
  lineage: "family",
  runtime: "runtime",
  observatory: "runtime",
  other: "runtime",
};

export function activityIconKind(event: LogEvent): ActivityIconKind {
  if (event.kind === "order.filled" || event.kind === "env_fill_settled") return "fill";
  // Covers runtime.compute_request_rejected (a wake denial) along with
  // anything else halted/rejected — one substring check, not a growing list
  // of exact-kind branches for the same "worth a second look" signal.
  if (event.kind.endsWith(".halted") || event.kind.includes("rejected")) return "alert";
  return ICON_BY_GROUP[groupForKind(event.kind)];
}

function str(
  payload: Readonly<Record<string, unknown>> | undefined,
  ...keys: string[]
): string | null {
  if (!payload) return null;
  for (const key of keys) {
    const value = payload[key];
    if (typeof value === "string" && value.length > 0) return value;
  }
  return null;
}

function num(
  payload: Readonly<Record<string, unknown>> | undefined,
  ...keys: string[]
): number | null {
  if (!payload) return null;
  for (const key of keys) {
    const value = payload[key];
    if (typeof value === "number" && Number.isFinite(value)) return value;
    if (typeof value === "string") {
      const parsed = Number.parseFloat(value);
      if (Number.isFinite(parsed)) return parsed;
    }
  }
  return null;
}

function arrLen(
  payload: Readonly<Record<string, unknown>> | undefined,
  key: string,
): number | null {
  const value = payload?.[key];
  return Array.isArray(value) ? value.length : null;
}

/** Delegates to the shared, sign-correct `formatMoney` (`format/number.ts`)
 * instead of a local reimplementation — an earlier draft's own version
 * prefixed `$` onto `toLocaleString`'s signed output, producing "$-123.45"
 * for a negative amount instead of "-$123.45" (code-review finding,
 * revamp self-review round 2). `null` stays `null` (not `formatMoney`'s
 * "–") so callers can keep treating "no value" as "omit this clause". */
function money(n: number | null): string | null {
  return n === null ? null : formatMoney(n);
}

/** Humanizes a `kind` string ("loop_tick" / "market.view" / "env_tariff_posted")
 * into title-cased words — the fallback every sentence below degrades to
 * when a payload doesn't carry the fields a more specific sentence needs.
 * Never fabricates details the payload doesn't have; it only ever
 * rephrases the kind name itself, which is always real. */
function humanizeKind(kind: string): string {
  const words = kind.replace(/[._]/g, " ").trim().split(/\s+/);
  return words.map((w, i) => (i === 0 ? w.charAt(0).toUpperCase() + w.slice(1) : w)).join(" ");
}

/** One human sentence per event, replacing the raw-JSON payload preview
 * (DIRECTION.md §1.3, owner examples: "Bought 3 SPY at $765.06," "Woke to
 * trade," "Reconciled with broker: consistent," "Semantic budget reached
 * for today"). Pure function of a single event's own kind/payload — never
 * cross-references other events, so it only ever states what this one
 * event's payload actually carries; a field a sentence would want but the
 * payload lacks means that sentence backs off to the next-most-specific
 * form rather than guessing. The full payload stays one click away in the
 * log panel's detail drawer (fidelity unchanged). */
export function activitySentence(event: LogEvent): string {
  const p = event.payload;
  const kind = event.kind;

  if (kind === "brain.wake") {
    const mode = str(p, "mode");
    const steps = num(p, "granted_steps");
    if (mode && steps !== null)
      return `Woke to ${mode} — ${steps} step${steps === 1 ? "" : "s"} granted`;
    if (mode) return `Woke to ${mode}`;
    return "Woke for a decision cycle";
  }
  if (kind === "brain.plasticity") {
    const updates = num(p, "updates_applied");
    return updates !== null
      ? `Updated synaptic plasticity — ${updates} update${updates === 1 ? "" : "s"} applied`
      : "Updated synaptic plasticity";
  }
  if (kind === "portfolio.snapshot") {
    const nav = money(num(p, "nav"));
    const positions = arrLen(p, "positions");
    const orders = num(p, "open_orders");
    const parts: string[] = [];
    if (nav) parts.push(`NAV ${nav}`);
    if (positions !== null) parts.push(`${positions} position${positions === 1 ? "" : "s"}`);
    if (orders !== null) parts.push(`${orders} open order${orders === 1 ? "" : "s"}`);
    return parts.length > 0 ? parts.join(" · ") : "Portfolio snapshot recorded";
  }
  if (kind === "env_account_opened") {
    // OQA: the live loops record the account opening at the deterministic ledger
    // epoch (it sorts under "Sep 14"), so the sentence says what it is.
    const capital = num(p, "trading_capital");
    const reserve = num(p, "reserve") ?? 0;
    return capital !== null
      ? `Account opened — ${formatMoney(capital + reserve)} opening capital (ledger epoch time)`
      : "Account opened (ledger epoch time)";
  }
  if (kind === "life.assessment") {
    const stage = str(p, "stage");
    return stage ? `Life check — stage ${stage}` : "Updated life-expectancy assessment";
  }
  if (kind === "family.assessment") {
    return "Updated family readiness assessment";
  }
  if (kind === "order.filled" || kind === "env_fill_settled") {
    const symbol = str(p, "symbol");
    const side = str(p, "side")?.toLowerCase();
    // OQA: quantity and price as recorded — a crypto fill is on the 1e-9 grid
    // ("Bought 6123.456789012 DOGE/USD at $0.145069", never "... at $0.15").
    const qtyRaw =
      str(p, "qty", "quantity", "filled_qty") ?? num(p, "qty", "quantity", "filled_qty");
    const priceRaw =
      str(p, "price", "fill_price", "avg_price") ?? num(p, "price", "fill_price", "avg_price");
    const qty = qtyRaw === null ? null : formatQuantity(qtyRaw);
    const price = priceRaw === null ? null : formatPrice(priceRaw, { currency: true });
    if (symbol && qty !== null && qty !== "–" && price && price !== "–") {
      const verb = side === "sell" ? "Sold" : side === "buy" ? "Bought" : "Filled";
      return `${verb} ${qty} ${symbol} at ${price}`;
    }
    return symbol ? `Order filled — ${symbol}` : "Order filled";
  }
  if (kind === "order.placed") {
    const symbol = str(p, "symbol");
    const side = str(p, "side");
    const qty = num(p, "qty", "quantity");
    if (symbol && side && qty !== null) return `Placed order — ${side} ${qty} ${symbol}`;
    return symbol ? `Placed order — ${symbol}` : "Placed an order";
  }
  if (kind === "order.cancelled" || kind === "order.canceled") {
    const symbol = str(p, "symbol");
    return symbol ? `Cancelled order — ${symbol}` : "Cancelled an order";
  }
  if (kind.startsWith("reconcile.")) {
    const status = str(p, "status", "reconciliation_status");
    return status ? `Reconciled with broker: ${status}` : "Reconciled with broker";
  }
  if (kind.startsWith("semantic.")) {
    if (kind.includes("budget")) return "Semantic budget reached for today";
    const route = str(p, "route");
    const accepted = p?.["accepted"];
    const outcome = typeof accepted === "boolean" ? (accepted ? "accepted" : "rejected") : null;
    if (route && outcome) return `Semantic call via ${route} — ${outcome}`;
    if (route) return `Semantic call via ${route}`;
    return "Semantic call recorded";
  }
  if (kind === "runtime.compute_request_rejected") {
    const reason = str(p, "reason");
    return reason ? `Wake denied — ${reason.replace(/_/g, " ")}` : "Wake denied";
  }
  if (kind === "runtime.heartbeat") {
    const status = str(p, "reconciliation_status");
    return status ? `Heartbeat — reconciliation ${status}` : "Heartbeat";
  }
  if (kind === "calendar.session_transition") {
    const to = str(p, "to_state");
    return to ? `Session changed to ${to.replace(/_/g, " ")}` : "Session changed";
  }
  if (kind === MARKET_BATCH_KIND) {
    // The synthetic collapsed row `buildActivityRowItems`'s flushMarketBatch
    // builds for a run of market.view events — never a real published kind.
    const count = num(p, "count");
    return count !== null
      ? `Quotes refreshed · ${count} symbol${count === 1 ? "" : "s"}`
      : "Quotes refreshed";
  }
  if (kind === "market.view") {
    const symbol = str(p, "symbol");
    const price = money(num(p, "price", "last_price", "mark"));
    if (symbol && price) return `${symbol} updated — ${price}`;
    return symbol ? `${symbol} market view updated` : "Market view updated";
  }
  if (kind.startsWith("env_tariff")) {
    const amount = money(num(p, "amount"));
    return amount ? `Compute/data cost charged — ${amount}` : "Compute/data cost charged";
  }
  if (kind.startsWith("lineage.")) {
    return humanizeKind(kind.slice("lineage.".length));
  }

  return humanizeKind(kind);
}

/** Given each filter chip's measured width (px, in document order) and the
 * space available in the sticky toolbar row, returns how many leading chips
 * fit on one line, reserving room for a trailing "+N more" button whenever
 * at least one chip doesn't fit. Pure arithmetic — no DOM — so the overflow
 * decision itself is unit-testable; `LogPanel.ts` supplies real
 * `getBoundingClientRect()` widths and rebuilds on `ResizeObserver`.
 * (Owner directive: "filter chips wrap into two lines" — this is the fix:
 * one row, overflow collected behind a menu instead of wrapping.) */
export function computeVisibleChipCount(
  chipWidthsPx: readonly number[],
  containerWidthPx: number,
  moreButtonWidthPx: number,
  gapPx: number,
): number {
  if (chipWidthsPx.length === 0) return 0;
  const totalWidth =
    chipWidthsPx.reduce((sum, w) => sum + w, 0) + gapPx * Math.max(0, chipWidthsPx.length - 1);
  if (totalWidth <= containerWidthPx) return chipWidthsPx.length;

  // At least one chip must overflow, so reserve room for the "+N more"
  // button up front.
  const budget = containerWidthPx - moreButtonWidthPx;
  let used = 0;
  let count = 0;
  for (const width of chipWidthsPx) {
    const next = used + (count > 0 ? gapPx : 0) + width;
    if (next > budget) break;
    used = next;
    count += 1;
  }
  return count;
}
